import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAssignUserTeamMutation,
  useTeamEligibleUsersQuery,
} from '@common/cache';
import { createTeamUserSchema } from '../schemas/createTeamUser.schema';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface IUseAddMembersProps {
  id: string;
}

interface ICreateTeamFormShape {
  member: string;
}

const initialValues: ICreateTeamFormShape = {
  member: '',
};

export const useAddMembers = ({ id }: IUseAddMembersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const formMethods = useForm<ICreateTeamFormShape>({
    mode: 'onChange',
    resolver: yupResolver(createTeamUserSchema),
    defaultValues: initialValues,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
    reset,
  } = formMethods;
  const assignUserTeamMutation = useAssignUserTeamMutation({ id });

  const { data: teamEligibleUsers = [] } = useTeamEligibleUsersQuery({
    teamId: id,
    search: searchQuery,
  });

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: ICreateTeamFormShape) => {
    try {
      await assignUserTeamMutation.mutateAsync({
        user_ids: [values.member],
        team_id: id,
      });
      handleClose();
      toast.success('You added a new member!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    control,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    teamEligibleUsers,
    watch,
    formMethods,
    searchQuery,
    setSearchQuery,
  };
};
